import { unionBy } from 'lodash'
import * as actions from './actions'
import { DEFAULT_LANGUAGE_OPTION, TRANSLATION_STATUS } from 'qap/constants'
import { success, failure } from 'utilities/actions'
import { isRelevantTranslation, mergeTranslations, updateCurrentTranslation } from './helpers'
import type { CustomFollowUpLabel, Report, ReportActivity } from 'types'
import { CLEAR_UNSAVED_CHANGES } from '../unsavedChanges/actions'

export type State = {
  data?: Report;
  relatedReports?: Report[];
  errors?: any[];
  commentCreateErrors?: any[];
  commentUpdateErrors?: any[];
  editMode: boolean;
  selectedLanguage: string;
  showCommentUpdateError: boolean;
  showCommentCreateError: boolean;
  editCommentId?: string;
  commentState?: {
    reportId?: number;
    text?: string;
    measurands?: string[];
    commentFilter?: string;
  };
  isCreatingComment: boolean;
  customFollowUpLabels: CustomFollowUpLabel[];
  activity: ReportActivity[];
  loadingActivity: boolean;
  activityCount?: number;
  activityPagination: {
    limit: number;
    offset: number;
  };
  isLoadingRelatedReports: boolean;
  isUpdatingReviewers: boolean;
  isUpdatingUserReviewStatus: boolean;
  capaState: {
    submitting: boolean;
    submitted: boolean;
    errors: any[];
    draft?: {
      values: any;
      dirtyFields: any;
    };
  };
};

export const initialState = {
  activityPagination: {
    limit: 100,
    offset: 0
  },
  loadingActivity: false,
  customFollowUpLabels: [],
  activity: [],
  editMode: false,
  isCreatingComment: false,
  showCommentCreateError: false,
  showCommentUpdateError: false,
  selectedLanguage: DEFAULT_LANGUAGE_OPTION,
  isLoadingRelatedReports: false,
  isUpdatingReviewers: false,
  isUpdatingUserReviewStatus: false,
  capaState: {
    submitting: false,
    submitted: false,
    errors: [],
  }
} as const

const reducer = (state: State, action: any) => {
// @ts-expect-error - TS2322 - Type '{ readonly activityPagination: { readonly limit: 100; readonly offset: 0; }; readonly loadingActivity: false; readonly customFollowUpLabels: readonly []; readonly activity: readonly []; readonly editMode: false; ... 7 more ...; readonly capaState: { ...; }; }' is not assignable to type 'State'.
  if (state === undefined) { state = initialState }
  switch (action.type) {
    case actions.CLEAR_REPORT_DATA:
    case actions.FETCH_REPORT:
      return initialState
    case actions.FETCH_RELATED_REPORTS:
      return { ...state, isLoadingRelatedReports: true }
    case actions.API_REPORTS_REVIEWERS_UPDATE:
      return { ...state, isUpdatingReviewers: true }
    case actions.API_REPORTS_USER_REVIEWS_CREATE:
      return { ...state, isUpdatingUserReviewStatus: true }
    case actions.SET_TRANSLATION_TARGET_LANGUAGE:
      return {
        ...state,
        selectedLanguage: action.payload.languageCode,
      }
    case actions.SET_REPORT_TRANSLATION:
      return {
        ...state,
        selectedLanguage: action.payload.languageCode,
        data: {
          ...state.data,
          pdfUrl: action.payload.url,
        },
        errors: [],
      }
    case actions.REQUIRE_REPORT_TRANSLATION:
      if (!state.data || !state.data.translations) return state

      return {
        ...state,
        data: {
          ...state.data,
          translations: updateCurrentTranslation(
            state,
            { status: TRANSLATION_STATUS.processing, url: '', error: '' }
          ),
        },
      }
    case actions.SET_TRANSLATION_ERROR:
      if (!isRelevantTranslation(state, action)) return state

      return {
        ...state,
        data: {
          ...state.data,
          translations: updateCurrentTranslation(
            state,
            { status: TRANSLATION_STATUS.failed, error: action.payload.error }
          ),
        },
      }
    case actions.API_REPORTS_COMMENTS_CREATE:
      return { ...state, isCreatingComment: true }
    case actions.SHOW_COMMENT_CREATE_ERROR:
      return { ...state, showCommentCreateError: action.payload.value }
    case actions.SHOW_COMMENT_UPDATE_ERROR:
      return { ...state, showCommentUpdateError: action.payload.value }
    case actions.SET_EDIT_MODE:
      const { isEditing, commentId } = action.payload
      return { ...state, editMode: isEditing, editCommentId: commentId }
    case actions.API_REPORTS_CAPAS_CREATE:
    case actions.API_REPORTS_CAPAS_UPDATE:
    case actions.API_REPORTS_CAPAS_CLOSE:
    case actions.API_REPORTS_CAPAS_REOPEN:
      return {
        ...state,
        capaState: { ...state.capaState, submitting: true, submitted: false, errors: [] },
      }

    case success(actions.API_REPORTS_ATTACHMENTS_UPDATE):
      const { attachments: currentAttachments } = state.data || {}
      const { attachments: newAttachments } = action.data
      const mergedAttachments = unionBy(newAttachments, currentAttachments, 'id')

      return {
        ...state,
        data: {
          ...state.data,
          attachments: mergedAttachments
        }
      }
    case success(actions.API_REPORTS_USER_REVIEWS_CREATE):
      return { ...state, data: action.data, isUpdatingUserReviewStatus: false }
    case success(actions.FETCH_REPORT):
    case success(actions.API_REPORTS_ATTACHMENTS_DESTROY):
    case success(actions.API_REPORTS_COMMENTS_DESTROY):
    case success(actions.API_REPORTS_REVIEW_STATUSES_UPDATE):
      return { ...state, data: action.data }
    case success(actions.API_REPORTS_REVIEWERS_UPDATE):
      return { ...state, data: action.data, isUpdatingReviewers: false }
    case success(actions.API_REPORTS_COMMENTS_CREATE):
      return { ...state, data: action.data, commentState: {}, isCreatingComment: false, showCommentCreateError: false, commentCreateErrors: [] }
    case success(actions.API_REPORTS_COMMENTS_UPDATE):
      return { ...state, data: action.data, editMode: false, showCommentUpdateError: false, commentUpdateErrors: [] }
    case success(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE):
      return { ...state, data: action.data }
    case success(actions.FETCH_RELATED_REPORTS):
      return { ...state, relatedReports: action.data, isLoadingRelatedReports: false }
    case success(actions.API_REPORTS_FOLLOW_UP_LABELS_INDEX):
      const { data: customFollowUpLabels = [] } = action
      return { ...state, customFollowUpLabels }
    case actions.API_REPORTS_ACTIVITY_INDEX:
      const { newOffset } = action
      return {
        ...state,
        loadingActivity: true,
        activityPagination: { ...state.activityPagination, offset: newOffset }
      }
    case success(actions.API_REPORTS_ACTIVITY_INDEX):
      const { data: activity = [], meta: { activityCount } } = action
      return { ...state, loadingActivity: false, activity, activityCount }
    case success(actions.POLL_REPORT_TRANSLATIONS):
      if (!isRelevantTranslation(state, action)) return state

      return {
        ...state,
        data: {
          ...state.data,
          pdfUrl: action.payload.url,
          translations: mergeTranslations(state.data?.translations, action.payload.translations),
        },
      }
    case success(actions.API_REPORTS_CAPAS_CREATE):
    case success(actions.API_REPORTS_CAPAS_UPDATE):
    case success(actions.API_REPORTS_CAPAS_CLOSE):
    case success(actions.API_REPORTS_CAPAS_REOPEN):
      const { reportReviewStatus, ...capaData } = action.data ?? {}
      return {
        ...state,
        data: {
          ...state.data,
// @ts-expect-error - TS18048 - 'state.data' is possibly 'undefined'. - fall through
          reviewStatus: reportReviewStatus ?? state.data.reviewStatus,
          capa: capaData
        },
        capaState: { ...state.capaState, submitting: false, submitted: true, errors: [], draft: undefined },
      }
    case failure(actions.API_REPORTS_USER_REVIEWS_CREATE):
      return { ...state, errors: action.errors, isUpdatingUserReviewStatus: false }
    case failure(actions.FETCH_REPORT):
    case failure(actions.FETCH_RELATED_REPORTS):
      return { ...state, errors: action.errors , isLoadingRelatedReports: false }
    case failure(actions.API_REPORTS_REVIEWERS_UPDATE):
      return { ...state, errors: action.errors, isUpdatingReviewers: false }
    case failure(actions.API_REPORTS_ATTACHMENTS_CREATE):
    case failure(actions.API_REPORTS_ATTACHMENTS_UPDATE):
    case failure(actions.API_REPORTS_ATTACHMENTS_DESTROY):
    case failure(actions.API_REPORTS_COMMENTS_DESTROY):
    case failure(actions.API_REPORTS_REVIEW_STATUSES_UPDATE):
    case failure(actions.API_REPORTS_FOLLOW_UP_LABELS_INDEX):
    case failure(actions.API_REPORTS_ACTIVITY_INDEX):
      return { ...state, loadingActivity: false, errors: action.errors }
    case failure(actions.API_REPORTS_COMMENTS_CREATE):
      return { ...state, isCreatingComment: false, errors: action.errors, showCommentCreateError: true, commentCreateErrors: action.errors }
    case failure(actions.API_REPORTS_COMMENTS_UPDATE):
      return { ...state, errors: action.errors, editMode: true, showCommentUpdateError: true, commentUpdateErrors: action.errors }
    case failure(actions.API_REPORTS_FOLLOW_UP_LABELS_UPDATE):
      return { ...state, errors: action.errors }
    case failure(actions.API_REPORTS_CAPAS_CREATE):
    case failure(actions.API_REPORTS_CAPAS_UPDATE):
    case failure(actions.API_REPORTS_CAPAS_CLOSE):
    case failure(actions.API_REPORTS_CAPAS_REOPEN):
      return {
        ...state,
        capaState: {
          ...state.capaState,
          submitting: false,
          submitted: false,
          errors: action.errors,
        },
      }

    case actions.SET_REPORT_CAPA_DRAFT:
      return {
        ...state,
        capaState: {
          ...state.capaState,
          draft: {
            values: action.draftData.values,
            dirtyFields: action.draftData.dirtyFields,
          },
        },
      }
    case CLEAR_UNSAVED_CHANGES:
    case actions.CLEAR_REPORT_CAPA_DRAFT:
      return {
        ...state,
        capaState: {
          ...state.capaState,
          draft: undefined,
        },
      }

    case actions.SET_REPORT_COMMENT_STATE:
      return { ...state, commentState: { ...state.commentState, ...action.commentState } }
    case actions.CLEAR_REPORT_COMMENT_STATE:
      const { commentState: deleted, ...newState } = state
      return newState
    default:
      return state
  }
}

export default reducer
